'use client';

import React, { useState, useMemo } from 'react';
import Image from 'next/image';
import { Resource } from '@/data/resources';
import { getColorFromString, truncate, getFaviconUrl } from '@/lib/utils';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/hooks/useTranslation';

// 缓存已失败的图标 URL
const failedIcons = new Set<string>();

interface ResourceCardProps {
  resource: Resource;
}

const ResourceCard = ({ resource }: ResourceCardProps) => {
  const bgColorClass = getColorFromString(resource.name);
  const [isHovering, setIsHovering] = useState(false);
  const [iconError, setIconError] = useState(false);
  const { t, language } = useTranslation();
  
  // Calculate favicon URL once when component mounts
  const faviconUrl = useMemo(() => {
    if (!resource.url) return null;
    return getFaviconUrl(resource.url, resource.icon);
  }, [resource.url, resource.icon]);
  
  // 获取翻译后的描述文本
  const translatedDescription = useMemo(() => {
    // 如果有 desc-id 的翻译，则使用翻译，否则使用原始描述
    const translatedDesc = t(`desc-${resource.id}`);
    return translatedDesc === `desc-${resource.id}` ? resource.description : translatedDesc;
  }, [t, resource.id, resource.description]);
  
  // Handle direct click to open link immediately
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(resource.url, '_blank', 'noopener,noreferrer');
  };

  // Handle image loading error
  const handleImageError = () => {
    if (faviconUrl) {
      failedIcons.add(faviconUrl);
    }
    setIconError(true);
  };
  
  // 判断是否应该显示图标
  const shouldShowIcon = useMemo(() => {
    // 如果没有图标 URL，不显示
    if (!faviconUrl) return false;
    
    // 如果该图标已经在失败列表中，不显示
    if (failedIcons.has(faviconUrl)) return false;
    
    // 如果当前组件实例的图标已经发生错误，不显示
    if (iconError) return false;
    
    return true;
  }, [faviconUrl, iconError]);
  
  return (
    <a 
      href={resource.url} 
      target="_blank" 
      rel="noopener noreferrer"
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className="block bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden cursor-pointer h-full"
    >
      <div className="p-4 flex items-start space-x-3 relative">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 relative rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            {shouldShowIcon ? (
              <Image 
                src={faviconUrl!}
                alt={resource.name} 
                width={24} 
                height={24}
                className="object-contain p-0.5"
                onError={handleImageError}
                unoptimized
              />
            ) : (
              <div className={`w-full h-full flex items-center justify-center text-white ${bgColorClass}`}>
                <span className="text-base font-semibold">
                  {resource.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center">
            <h3 className="text-base font-medium text-gray-900 dark:text-white mr-1 truncate">
              {resource.name}
            </h3>
            <ArrowTopRightOnSquareIcon className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-opacity duration-200 ${isHovering ? 'opacity-100' : 'opacity-0'}`} />
          </div>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
            {truncate(translatedDescription, 70)}
          </p>
        </div>
      </div>
    </a>
  );
};

export default ResourceCard; 